/* ==========================================================================
   Carrusel de videos cortos (sections/vz-video-carousel.liquid)

   Tres comportamientos, según el ajuste "Al pulsar un video":

     popup -> todos parados; al pulsar uno se abre en grande en el centro
     click -> todos parados; al pulsar uno se reproduce ahí y se para el anterior
     view  -> se reproducen solos y sin sonido al entrar en pantalla

   TikTok, YouTube y Vimeo solo cargan su reproductor al pulsar.
   ========================================================================== */
(function () {
  'use strict';

  var READY_CLASS = 'vz-vid--ready';

  function parseOptions(el) {
    var raw = el.getAttribute('data-options');
    if (!raw) return {};
    try {
      return JSON.parse(raw);
    } catch (e) {
      return {};
    }
  }

  function toNumber(value, fallback) {
    var n = parseFloat(value);
    return isNaN(n) ? fallback : n;
  }

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /* Dirección del reproductor incrustado según la plataforma. */
  function embedSrc(type, id) {
    if (!id) return '';
    if (type === 'tiktok') return 'https://www.tiktok.com/embed/v2/' + id;
    if (type === 'vimeo') return 'https://player.vimeo.com/video/' + id + '?autoplay=1&playsinline=1';
    return 'https://www.youtube.com/embed/' + id + '?autoplay=1&playsinline=1&rel=0';
  }

  function buildIframe(type, id, title) {
    var iframe = document.createElement('iframe');
    iframe.setAttribute('src', embedSrc(type, id));
    iframe.setAttribute('allow', 'autoplay; encrypted-media; picture-in-picture; fullscreen');
    iframe.setAttribute('allowfullscreen', '');
    iframe.setAttribute('title', title || 'Video');
    iframe.setAttribute('frameborder', '0');
    return iframe;
  }

  var VzVideoCarousel = (function () {
    function VzVideoCarousel(el) {
      this.el = el;
      this.options = parseOptions(el);
      this.mode = this.options.mode || 'popup';
      this.swiperEl = el.querySelector('[data-vz-swiper]');
      this.cards = Array.prototype.slice.call(el.querySelectorAll('[data-vz-card]'));
      this.modal = el.querySelector('[data-vz-modal]');
      this.stage = el.querySelector('[data-vz-modal-stage]');
      this.swiper = null;
      this.observer = null;

      this.onKeydown = this.onKeydown.bind(this);
      this.onResize = this.positionArrows.bind(this);

      this.initSwiper();
      this.initCards();
      this.initModal();
      if (this.mode === 'view') this.initAutoplay();
      this.positionArrows();

      window.addEventListener('resize', this.onResize);
    }

    /* ------------------------------------------------------------------ */
    VzVideoCarousel.prototype.initSwiper = function () {
      if (!this.swiperEl || typeof Swiper === 'undefined') return;

      var o = this.options;
      var slides = this.el.querySelectorAll('.vz-vid__slide').length;
      var perView = toNumber(o.desktop, 6);
      var gap = toNumber(o.gap, 12);

      // Swiper necesita más diapositivas que las visibles para clonar y dar la
      // vuelta sin huecos. Si no hay suficientes, se queda sin bucle en vez de
      // romperse.
      var loop = !!o.loop && slides > perView;

      var config = {
        slidesPerView: toNumber(o.mobile, 2),
        spaceBetween: gap,
        loop: loop,
        loopAdditionalSlides: loop ? slides : 0,
        rewind: !!o.loop && !loop,
        centeredSlides: !!o.centered,
        watchSlidesProgress: true,
        grabCursor: true,
        breakpoints: {
          750: { slidesPerView: toNumber(o.tablet, 3) },
          990: { slidesPerView: toNumber(o.laptop, 5) },
          1200: { slidesPerView: perView }
        }
      };

      var next = this.el.querySelector('[data-vz-next]');
      var prev = this.el.querySelector('[data-vz-prev]');
      if (next && prev) {
        config.navigation = { nextEl: next, prevEl: prev, disabledClass: 'is-disabled' };
      }

      var pagination = this.el.querySelector('[data-vz-pagination]');
      if (pagination) {
        config.pagination = { el: pagination, clickable: true };
      }

      if (o.autoplaySlider && !prefersReducedMotion()) {
        config.autoplay = {
          delay: toNumber(o.autoplayDelay, 5000),
          disableOnInteraction: false,
          pauseOnMouseEnter: true
        };
      }

      var self = this;
      // Al cambiar de diapositiva se para lo que se estuviera viendo, para no
      // dejar audio sonando fuera de pantalla.
      config.on = {
        slideChange: function () {
          if (self.mode === 'click') self.pauseAll();
        }
      };

      this.swiper = new Swiper(this.swiperEl, config);
      this.el.classList.add(READY_CLASS);
    };

    /* ------------------------------------------------------------------
       Pulsar un video
       ------------------------------------------------------------------ */
    VzVideoCarousel.prototype.initCards = function () {
      var self = this;

      this.cards.forEach(function (card) {
        var video = card.querySelector('[data-vz-video]');
        var playBtn = card.querySelector('[data-vz-play]');
        var soundBtn = card.querySelector('[data-vz-sound]');

        function activate(event) {
          if (event) {
            event.preventDefault();
            event.stopPropagation();
          }

          if (self.mode === 'popup') {
            self.openModal(card);
            return;
          }

          // Modo "click" y modo "view": alterna reproducción en la tarjeta.
          if (card.getAttribute('data-vz-embed')) {
            self.loadEmbedInCard(card);
            return;
          }
          if (!video) return;

          if (video.paused) {
            self.pauseAll(card);
            if (self.mode === 'click' && self.options.soundOnClick) {
              video.muted = false;
              if (soundBtn) soundBtn.setAttribute('aria-pressed', 'true');
            }
            self.play(card, video);
          } else {
            video.pause();
          }
        }

        if (playBtn) playBtn.addEventListener('click', activate);

        // En los modos de pulsar, toda la miniatura responde al clic.
        if (self.mode !== 'view') {
          var media = card.querySelector('.vz-vid__media');
          if (media) media.addEventListener('click', activate);
        }

        if (video) {
          video.addEventListener('play', function () {
            card.classList.add('is-playing');
          });
          video.addEventListener('pause', function () {
            card.classList.remove('is-playing');
          });
          video.addEventListener('ended', function () {
            card.classList.remove('is-playing');
          });
        }

        if (soundBtn) {
          soundBtn.addEventListener('click', function (event) {
            event.preventDefault();
            event.stopPropagation();
            if (!video) return;

            var unmute = video.muted;
            if (unmute) self.muteAll();
            video.muted = !unmute;
            soundBtn.setAttribute('aria-pressed', unmute ? 'true' : 'false');
            if (unmute && video.paused) self.play(card, video);
          });
        }
      });
    };

    VzVideoCarousel.prototype.play = function (card, video) {
      if (video.preload === 'none') video.preload = 'auto';
      var promise = video.play();
      if (promise && typeof promise.catch === 'function') {
        promise.catch(function () {
          // Si el navegador bloquea el sonido, se reintenta en silencio.
          if (!video.muted) {
            video.muted = true;
            var btn = card.querySelector('[data-vz-sound]');
            if (btn) btn.setAttribute('aria-pressed', 'false');
            var retry = video.play();
            if (retry && typeof retry.catch === 'function') {
              retry.catch(function () {
                card.classList.remove('is-playing');
              });
            }
          } else {
            card.classList.remove('is-playing');
          }
        });
      }
    };

    /* Para todo menos la tarjeta indicada. */
    VzVideoCarousel.prototype.pauseAll = function (except) {
      this.cards.forEach(function (card) {
        if (card === except) return;
        var video = card.querySelector('[data-vz-video]');
        if (video && !video.paused) video.pause();
        if (video) video.muted = true;

        var slot = card.querySelector('[data-vz-embed-slot]');
        if (slot) slot.innerHTML = '';

        var btn = card.querySelector('[data-vz-sound]');
        if (btn) btn.setAttribute('aria-pressed', 'false');
        card.classList.remove('is-playing');
      });
    };

    VzVideoCarousel.prototype.muteAll = function () {
      this.cards.forEach(function (card) {
        var video = card.querySelector('[data-vz-video]');
        var btn = card.querySelector('[data-vz-sound]');
        if (video) video.muted = true;
        if (btn) btn.setAttribute('aria-pressed', 'false');
      });
    };

    /* Reproductor de terceros dentro de la propia miniatura (modo "click"). */
    VzVideoCarousel.prototype.loadEmbedInCard = function (card) {
      var slot = card.querySelector('[data-vz-embed-slot]');
      if (!slot) return;

      if (slot.querySelector('iframe')) {
        slot.innerHTML = '';
        card.classList.remove('is-playing');
        return;
      }

      this.pauseAll(card);
      slot.appendChild(
        buildIframe(
          card.getAttribute('data-vz-embed'),
          card.getAttribute('data-vz-embed-id'),
          card.getAttribute('data-vz-title')
        )
      );
      card.classList.add('is-playing');
    };

    /* ------------------------------------------------------------------
       Ventana central (modo "popup")
       ------------------------------------------------------------------ */
    VzVideoCarousel.prototype.initModal = function () {
      if (!this.modal) return;
      var self = this;

      this.modal.querySelectorAll('[data-vz-modal-close]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          self.closeModal();
        });
      });
    };

    VzVideoCarousel.prototype.openModal = function (card) {
      if (!this.modal || !this.stage) return;

      this.pauseAll();
      this.stage.innerHTML = '';

      var embedType = card.getAttribute('data-vz-embed');
      var src = card.getAttribute('data-vz-src');

      if (embedType) {
        this.stage.appendChild(
          buildIframe(embedType, card.getAttribute('data-vz-embed-id'), card.getAttribute('data-vz-title'))
        );
        this.stage.classList.toggle('vz-vid__modal-stage--tiktok', embedType === 'tiktok');
      } else if (src) {
        var video = document.createElement('video');
        video.setAttribute('playsinline', '');
        video.setAttribute('controls', '');
        video.setAttribute('autoplay', '');
        if (this.options.loopVideo) video.setAttribute('loop', '');
        if (!this.options.soundOnClick) video.muted = true;

        var poster = card.querySelector('.vz-vid__video');
        if (poster && poster.getAttribute('poster')) {
          video.setAttribute('poster', poster.getAttribute('poster'));
        }

        var source = document.createElement('source');
        source.setAttribute('src', src);
        source.setAttribute('type', 'video/mp4');
        video.appendChild(source);

        this.stage.appendChild(video);
        this.stage.classList.remove('vz-vid__modal-stage--tiktok');

        var promise = video.play();
        if (promise && typeof promise.catch === 'function') {
          promise.catch(function () {
            video.muted = true;
            video.play().catch(function () {});
          });
        }
      } else {
        return;
      }

      this.modal.hidden = false;
      document.body.classList.add('vz-vid-modal-open');
      document.addEventListener('keydown', this.onKeydown);

      var close = this.modal.querySelector('.vz-vid__modal-close');
      if (close) close.focus();

      if (this.swiper && this.swiper.autoplay) this.swiper.autoplay.stop();
    };

    VzVideoCarousel.prototype.closeModal = function () {
      if (!this.modal) return;

      this.modal.hidden = true;
      // Vaciar la ventana corta el sonido del video o del iframe.
      if (this.stage) this.stage.innerHTML = '';
      document.body.classList.remove('vz-vid-modal-open');
      document.removeEventListener('keydown', this.onKeydown);

      if (this.swiper && this.swiper.autoplay && this.options.autoplaySlider) {
        this.swiper.autoplay.start();
      }
    };

    VzVideoCarousel.prototype.onKeydown = function (event) {
      if (event.key === 'Escape' || event.keyCode === 27) this.closeModal();
    };

    /* ------------------------------------------------------------------
       Modo "view": reproducción automática al entrar en pantalla
       ------------------------------------------------------------------ */
    VzVideoCarousel.prototype.initAutoplay = function () {
      if (!('IntersectionObserver' in window)) return;
      var self = this;

      this.observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            var card = entry.target;
            var video = card.querySelector('[data-vz-video]');
            if (!video) return;

            if (entry.isIntersecting) {
              self.play(card, video);
            } else {
              video.pause();
              video.muted = true;
              var soundBtn = card.querySelector('[data-vz-sound]');
              if (soundBtn) soundBtn.setAttribute('aria-pressed', 'false');
            }
          });
        },
        { threshold: 0.5 }
      );

      this.cards.forEach(
        function (card) {
          if (card.querySelector('[data-vz-video]')) this.observer.observe(card);
        }.bind(this)
      );
    };

    /* ------------------------------------------------------------------
       Las flechas se centran sobre el video, no sobre la tarjeta entera
       (que incluye el texto y el producto de debajo).
       ------------------------------------------------------------------ */
    VzVideoCarousel.prototype.positionArrows = function () {
      var media = this.el.querySelector('.vz-vid__media');
      if (!media) return;
      var height = media.getBoundingClientRect().height;
      if (height) this.el.style.setProperty('--vz-vid-arrow-top', height / 2 + 'px');
    };

    VzVideoCarousel.prototype.destroy = function () {
      window.removeEventListener('resize', this.onResize);
      document.removeEventListener('keydown', this.onKeydown);
      document.body.classList.remove('vz-vid-modal-open');
      if (this.observer) this.observer.disconnect();
      if (this.swiper && this.swiper.destroy) this.swiper.destroy(true, true);
    };

    return VzVideoCarousel;
  })();

  /* -------------------------------------------------------------------- */
  function init(root) {
    (root || document).querySelectorAll('vz-video-carousel').forEach(function (el) {
      if (el.vzCarousel) return;
      el.vzCarousel = new VzVideoCarousel(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    event.target.querySelectorAll('vz-video-carousel').forEach(function (el) {
      if (el.vzCarousel) el.vzCarousel.destroy();
    });
  });
})();
